import { createConfig, http, useAccount, useChainId, useConnect, useConnectors, useConnectorClient, useDisconnect } from "wagmi";
import { injected } from "wagmi/connectors";
import { defineChain, formatUnits, erc20Abi } from "viem";
import { getBalance, readContract } from "viem/actions";
import { ARC_RPC_URL, ACTRA_CONTRACT_ADDRESS } from "@/config/actra";

// ============================================================
// ACTRA WALLET LAYER — wagmi + viem, isolated architecture
// Target: Arc Mainnet (chain 5042), native gas/settlement USDC.
// Injected EIP-6963 wallets: MetaMask, Rabby, OKX, etc.
// WalletConnect remote pairing requires a project ID — wire the
// walletConnect() connector here when one is supplied.
//
// Balance reads go through the connected wallet's own provider
// (connector client), so they work even if the public RPC is
// unreachable. getACTRABalance reads the live $ACTRA contract
// (balanceOf + decimals via erc20Abi). writeContract stays a
// stub until task/proof contract functions ship.
// ============================================================

export const ARC_CHAIN_ID = 5042;
export const ARC_CHAIN_ID_HEX = "0x13B2";

export const arc = defineChain({
    id: 5042,
    name: "Arc",
    nativeCurrency: { name: "USD Coin", symbol: "USDC", decimals: 6 },
    rpcUrls: { default: { http: ARC_RPC_URL ? [ARC_RPC_URL] : [] } },
});

export const wagmiConfig = createConfig({
    chains: [arc],
    connectors: [injected()],
    transports: { [arc.id]: http(ARC_RPC_URL || undefined) },
    multiInjectedProviderDiscovery: true,
});

export function useArcWallet() {
    const account = useAccount();
    const chainId = useChainId();
    const connectors = useConnectors();
    const { connect, isPending: connecting, error: connectError } = useConnect();
    const { disconnect } = useDisconnect();
    const { data: client } = useConnectorClient();

    const isWrongNetwork = account.isConnected && chainId !== ARC_CHAIN_ID;
    const walletOptions = connectors.filter((c) => c.type === "injected");

    const connectWallet = (connector) => {
        const target = connector || walletOptions[0];
        if (!target) throw new Error("NO_WALLET");
        connect({ connector: target });
    };

    const switchToArc = async () => {
        if (!client) throw new Error("CONNECT FIRST");
        try {
            await client.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: ARC_CHAIN_ID_HEX }],
            });
        } catch (error) {
            if (error?.code !== 4902) throw error;
            if (!ARC_RPC_URL) throw new Error("ARC RPC URL NOT CONFIGURED");
            await client.request({
                method: "wallet_addEthereumChain",
                params: [
                    {
                        chainId: ARC_CHAIN_ID_HEX,
                        chainName: arc.name,
                        nativeCurrency: arc.nativeCurrency,
                        rpcUrls: [ARC_RPC_URL],
                    },
                ],
            });
            await client.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: ARC_CHAIN_ID_HEX }],
            });
        }
    };

    const getUSDCBalance = async (address) => {
        const target = address || account.address;
        if (!client || !target) throw new Error("CONNECT FIRST");
        if (chainId !== ARC_CHAIN_ID) throw new Error("WRONG NETWORK");
        return getBalance(client, { address: target });
    };

    const getACTRABalance = async (address) => {
        const target = address || account.address;
        if (!ACTRA_CONTRACT_ADDRESS) throw new Error("CONTRACT NOT SET");
        if (!client || !target) throw new Error("CONNECT FIRST");
        if (chainId !== ARC_CHAIN_ID) throw new Error("WRONG NETWORK");
        const [raw, decimals] = await Promise.all([
            readContract(client, { abi: erc20Abi, address: ACTRA_CONTRACT_ADDRESS, functionName: "balanceOf", args: [target] }),
            readContract(client, { abi: erc20Abi, address: ACTRA_CONTRACT_ADDRESS, functionName: "decimals" }),
        ]);
        return formatUnits(raw, decimals);
    };

    return {
        ...account,
        chainId,
        isWrongNetwork,
        walletOptions,
        connecting,
        connectError,
        connectWallet,
        switchToArc,
        getUSDCBalance,
        getACTRABalance,
        disconnectWallet: disconnect,
    };
}

// writeContract stays stubbed until on-chain task/proof functions ship.
export async function writeContract() {
    throw new Error("NOT_IMPLEMENTED");
}

export { formatUnits };

